import { Checkbox } from '../../../../components/Checkbox';
import { tourAnchor } from '../../../tours/tourAnchors';
import { useTour } from '../../../tours/useTour';
import { ROSTER_IMPORT_FEATURE, ROSTER_IMPORT_TOURS } from './rosterImportTours';
import './UploadTypeStep.css';

export type UploadType = 'single_team' | 'showcase' | 'multi_team';

interface UploadTypeStepProps {
  selected: UploadType;
  onSelect: (type: UploadType) => void;
  /**
   * Whether the coach has declared this a junior college upload.
   *
   * Deliberately NOT a fourth `UploadType`. That enum describes STRUCTURE - one team or many -
   * while this describes what kind of program, and the two are independent: a showcase of JUCO
   * teams is a real thing, and the scout book this feature was built from is exactly one. Folding
   * JUCO into the same axis would make that unsayable.
   */
  isJucoUpload: boolean;
  onJucoUploadChange: (isJuco: boolean) => void;
  onContinue: () => void;
}

const OPTIONS: { type: UploadType; title: string; description: string }[] = [
  {
    type: 'single_team',
    title: 'Team Roster',
    description: 'A roster for a single team',
  },
  {
    type: 'showcase',
    title: 'Showcase / Event',
    description: 'A showcase or event with multiple teams',
  },
  {
    type: 'multi_team',
    title: 'Database Upload',
    description: 'Players from different teams (spreadsheet, database export)',
  },
];

export function UploadTypeStep({
  selected,
  onSelect,
  isJucoUpload,
  onJucoUploadChange,
  onContinue,
}: UploadTypeStepProps) {
  // Nothing on this step loads, so its tour can start as soon as the step is on screen.
  useTour(ROSTER_IMPORT_FEATURE, {
    ready: true,
    hasData: false,
    pageTourKey: ROSTER_IMPORT_TOURS.uploadType,
    dataTourKey: null,
  });

  return (
    <div className="upload-type-step">
      <h3 className="upload-type-title">What are you uploading?</h3>
      <p className="upload-type-subtitle">This helps us extract your data more accurately</p>

      <div className="upload-type-options" {...tourAnchor('roster-import.type.options')}>
        {OPTIONS.map((option) => (
          <label
            key={option.type}
            className={`upload-type-option ${selected === option.type ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="upload-type"
              value={option.type}
              checked={selected === option.type}
              onChange={() => onSelect(option.type)}
            />
            <div className="upload-type-radio">
              <div className="upload-type-radio-inner" />
            </div>
            <div className="upload-type-option-text">
              <span className="upload-type-option-title">{option.title}</span>
              <span className="upload-type-option-description">{option.description}</span>
            </div>
          </label>
        ))}
      </div>

      {/*
        Declared here because this is where the coach already knows it - they are holding the
        scout book. It PRE-ARMS the per-team toggles rather than replacing them: a showcase batch
        is genuinely mixed (the book carries a prep program among the junior colleges), so every
        team is still individually correctable at the review step.
      */}
      <div className="upload-type-juco" {...tourAnchor('roster-import.type.juco')}>
        <Checkbox
          checked={isJucoUpload}
          onChange={(e) => onJucoUploadChange(e.target.checked)}
          label={
            <span>
              <strong>These are junior college rosters</strong>
              <span className="upload-type-juco-hint">
                {' '}— players are stored as JUCO at the JC year their Class column names. You can
                still change this per team before importing.
              </span>
            </span>
          }
        />
      </div>

      <div className="upload-type-actions">
        <button
          className="primary-button"
          onClick={onContinue}
          {...tourAnchor('roster-import.type.continue')}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
